import { useMemo, useCallback } from 'react'
import { useParams, Link, useSearch, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { mdiAlert, mdiClose, mdiEyeOutline, mdiMagnify, mdiPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { usePropertyDefinitions, usePropertyCategories, type PropertyCategory } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { useApi } from '../api/hooks'
import { formatCreated } from '../lib/format'
import { PropertyImport } from './PropertyImport'
import { CategoryManagementDialog } from './CategoryManagementDialog'
import { $api } from '../api/queryClient'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

export function PropertiesPage() {
  const { org } = useParams({ strict: false })
  const search = useSearch({ from: '/$org/properties/' })
  const navigate = useNavigate({ from: '/$org/properties/' })
  const { toast } = useToast()
  const { request } = useApi()
  const queryClient = useQueryClient()
  const { t } = useTranslation('properties')

  // Parse data types from search params
  const selectedDataTypes = useMemo(() => {
    return search.data_type ? search.data_type.split(',') : []
  }, [search.data_type])

  // Apply filters to the query
  const filterOptions = useMemo(() => {
    const options: {
      entityType?: 'asset' | 'document' | 'user'
      dataTypes?: string[]
      search?: string
      categoryUid?: string
    } = {}

    if (search.entity_type) {
      options.entityType = search.entity_type
    }

    if (selectedDataTypes.length > 0) {
      options.dataTypes = selectedDataTypes
    }

    if (search.q?.trim()) {
      options.search = search.q.trim()
    }

    if (search.category) {
      options.categoryUid = search.category
    }

    return Object.keys(options).length > 0 ? options : undefined
  }, [search.q, search.entity_type, selectedDataTypes, search.category])

  const { data: propertyDefinitions, isLoading, error } = usePropertyDefinitions(org || 'demo', filterOptions)

  // Fetch categories for filter and display
  const { data: categories = [] } = usePropertyCategories(org || 'demo')

  // Helper function to get category name by UID
  const getCategoryName = useCallback((categoryUid: string | null | undefined): string | null => {
    if (!categoryUid) return null
    const category = categories.find((c: PropertyCategory) => c.uid === categoryUid)
    if (!category) return null
    if (typeof category.name === 'object' && category.name !== null) {
      const names = category.name as Record<string, string>
      return names.en || names.fr || Object.values(names)[0] || category.code || null
    }
    return String(category.name) || category.code || null
  }, [categories])

  // Fetch derivations to show rule counts for derived properties
  const { data: derivationsData } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/derivations', {
    params: { path: { orgCode: org! }, query: { limit: 200 } as any },
  })

  // Build a map: target property code -> { count, uid (if single derivation) }
  const derivationsByTarget = useMemo(() => {
    const derivations = (derivationsData as any)?.data || []
    const map: Record<string, { count: number; uid?: string }> = {}
    for (const d of derivations) {
      const code = d.target_property?.code
      if (!code) continue
      if (!map[code]) map[code] = { count: 0 }
      map[code].count += (d.rules_count || 0)
      if (!map[code].uid) map[code].uid = d.uid
    }
    return map
  }, [derivationsData])

  // Delete mutation
  const deletePropertyMutation = useMutation({
    mutationFn: async (propertyUid: string) => {
      await request('/api/v1/organizations/{orgCode}/properties/{propertyUid}', 'DELETE', {
        params: {
          path: {
            orgCode: org || 'demo',
            propertyUid: propertyUid
          }
        }
      })
      return propertyUid
    },
    onSuccess: () => {
      // Invalidate property definitions list
      queryClient.invalidateQueries({ queryKey: ['propertyDefinitions', org] })
    },
    onError: (error) => {
      console.error('Error deleting property:', error)
      toast({
        title: t('common:status.error'),
        description: t('list.delete_error'),
        variant: "destructive",
      })
    },
  })

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('mapping_rules.breadcrumb_home'), path: `/${org}` },
    { title: t('mapping_rules.breadcrumb_settings'), path: `/${org}/settings` },
    { title: t('list.title') }
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  // Helper function to handle multilingual property names
  const getPropertyName = (property: any) => {
    if (!property?.name) return property?.code || 'Unnamed Property'

    // Handle multilingual names
    if (typeof property.name === 'object' && property.name !== null) {
      const names = property.name as Record<string, string>
      return names.en || names.fr || Object.values(names)[0] || property.code || 'Unnamed Property'
    }

    return property.name || property.code || 'Unnamed Property'
  }

  // Helper function to handle multilingual descriptions
  const getPropertyDescription = (property: any) => {
    if (!property?.description) return ''

    // Handle multilingual descriptions
    if (typeof property.description === 'object' && property.description !== null) {
      const descriptions = property.description as Record<string, string>
      return descriptions.en || descriptions.fr || Object.values(descriptions)[0] || ''
    }

    return property.description || ''
  }

  // Update URL search params
  // `replace` swaps the current history entry instead of pushing one: the
  // search box calls it per keystroke, which otherwise left one entry per
  // letter: Back stepped through every prefix of the query, each step a new
  // PostHog pageview (#1721).
  const updateSearch = useCallback((updates: Partial<typeof search>, { replace = false }: { replace?: boolean } = {}) => {
    const newSearch = { ...search, ...updates }

    // Remove undefined values
    Object.keys(newSearch).forEach(key => {
      if (newSearch[key as keyof typeof newSearch] === undefined || newSearch[key as keyof typeof newSearch] === '') {
        delete newSearch[key as keyof typeof newSearch]
      }
    })

    navigate({ search: newSearch, replace })
  }, [search, navigate])

  // Helper functions for filters
  const handleSearchChange = useCallback((value: string) => {
    updateSearch({ q: value || undefined }, { replace: true })
  }, [updateSearch])

  const handleEntityTypeChange = useCallback((value: string) => {
    updateSearch({ entity_type: value === 'all' ? undefined : value as 'asset' | 'document' | 'user' })
  }, [updateSearch])

  const handleDataTypeToggle = useCallback((dataType: string) => {
    const newDataTypes = selectedDataTypes.includes(dataType)
      ? selectedDataTypes.filter(type => type !== dataType)
      : [...selectedDataTypes, dataType]

    updateSearch({ data_type: newDataTypes.length > 0 ? newDataTypes.join(',') : undefined })
  }, [selectedDataTypes, updateSearch])

  const handleCategoryChange = useCallback((value: string) => {
    updateSearch({ category: value === 'all' ? undefined : value })
  }, [updateSearch])

  const clearAllFilters = useCallback(() => {
    navigate({ search: {} })
  }, [navigate])

  const hasActiveFilters = search.q?.trim() || selectedDataTypes.length > 0 || search.entity_type || search.category

  const handleDelete = async (property: any) => {
    if (!confirm(t('list.delete_confirm', { name: getPropertyName(property) }))) {
      return
    }

    deletePropertyMutation.mutate(property.uid)
  }

  return (
    <div className="container mx-auto py-8 max-w-7xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.properties}
            title={t('list.title')}
            description={t('list.subtitle')}
          />
        </div>

        <div className="p-6">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-medium text-foreground">{t('list.section_title')}</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {t('list.section_subtitle')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Can perm="configuration.write">
                  <CategoryManagementDialog org={org || 'demo'} />
                </Can>
                <Can perm="configuration.write">
                  <PropertyImport />
                </Can>
                <Can perm="configuration.write">
                  <Link to={`/${org}/properties/create` as any}>
                    <Button data-testid="add-property">
                      <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                      {t('list.add_property')}
                    </Button>
                  </Link>
                </Can>
              </div>
            </div>

            {/* Filters Section */}
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
                {/* Search Input */}
                <div className="flex-1">
                  <div className="relative">
                    <AppIcon path={mdiMagnify} size="sm" className="absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <Input
                      placeholder={t('list.search_placeholder')}
                      value={search.q || ''}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-10"
                      data-testid="property-search"
                    />
                  </div>
                </div>

                {/* Entity Type Filter */}
                <div className="w-full md:w-48">
                  <Select
                    value={search.entity_type || 'all'}
                    onValueChange={handleEntityTypeChange}
                  >
                    <SelectTrigger data-testid="entity-type-filter">
                      <SelectValue placeholder={t('list.filter_all_entity_types')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('list.filter_all_entity_types')}</SelectItem>
                      <SelectItem value="asset">{t('entity_types.asset')}</SelectItem>
                      <SelectItem value="document">{t('entity_types.document')}</SelectItem>
                      <SelectItem value="user">{t('entity_types.user')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Filter */}
                {categories.length > 0 && (
                  <div className="w-full md:w-48">
                    <Select
                      value={search.category || 'all'}
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger data-testid="category-filter">
                        <SelectValue placeholder={t('list.filter_all_categories')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('list.filter_all_categories')}</SelectItem>
                        {categories.map((category: PropertyCategory) => (
                          <SelectItem key={category.uid} value={category.uid!}>
                            {getCategoryName(category.uid) || category.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Clear Filters Button */}
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    data-testid="clear-filters"
                  >
                    <AppIcon path={mdiClose} size="sm" className="mr-2" />
                    {t('list.clear_filters')}
                  </Button>
                )}
              </div>

              {/* Data Type Filter Pills */}
              <div>
                <p className="text-sm font-medium text-foreground mb-2">{t('list.filter_data_type_label')}</p>
                <div className="flex flex-wrap gap-2">
                  {(['text', 'number', 'boolean', 'date', 'enum', 'json'] as const).map((type) => (
                    <Button
                      key={type}
                      variant={selectedDataTypes.includes(type) ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDataTypeToggle(type)}
                      data-testid={`data-type-filter-${type}`}
                    >
                      {t(`data_types.${type}`)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-card shadow rounded-md border border-border overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('list.column_name')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('list.column_code')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('list.column_type')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('list.column_category')}
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      {t('list.column_created')}
                    </th>
                    <th className="sticky right-0 z-10 bg-muted/50 px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider shadow-[inset_1px_0_0_0_var(--border)]">
                      {t('list.column_actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                          <span className="text-muted-foreground">{t('list.loading')}</span>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="h-12 w-12 text-destructive mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-destructive mb-2">
                            {t('list.error')}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(error as any)?.title || (error as any)?.message || 'Unknown error occurred'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : propertyDefinitions && propertyDefinitions.length > 0 ? (
                    propertyDefinitions.map((property) => (
                      <tr key={property.uid} className={`hover:bg-muted/50 ${!property.enabled ? 'opacity-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/${org}/properties/${property.uid}` as any}
                              className={`font-medium hover:text-primary ${!property.enabled ? 'text-muted-foreground' : ''}`}
                              data-testid={`property-name-${property.uid}`}
                            >
                              {getPropertyName(property)}
                              {!property.enabled && <span className="ml-2 text-muted-foreground">({t('list.disabled')})</span>}
                            </Link>
                            {property.flags?.includes('derived') && (
                              <>
                                <Badge variant="secondary" className="text-xs" data-testid={`derived-badge-${property.uid}`}>{t('list.derived')}</Badge>
                                {(() => {
                                  const info = derivationsByTarget[property.code]
                                  if (info && info.count > 0) {
                                    return (
                                      <Link
                                        to={info.uid ? `/${org}/derivations/${info.uid}` as any : `/${org}/derivations` as any}
                                        className="text-xs text-primary hover:underline"
                                        data-testid={`derivation-rules-link-${property.uid}`}
                                      >
                                        {t('list.rules_count_other', { count: info.count })}
                                      </Link>
                                    )
                                  }
                                  return (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="flex items-center gap-0.5 text-xs text-amber-600" data-testid={`no-rules-warning-${property.uid}`}>
                                            <AppIcon path={mdiAlert} size="xs" />
                                            0 rules
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{t('list.no_rules_warning')}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )
                                })()}
                              </>
                            )}
                          </div>
                          {getPropertyDescription(property) && (
                            <p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">
                              {getPropertyDescription(property)}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 break-all text-sm text-muted-foreground max-w-[20rem]">
                          <code className="text-xs bg-muted px-2 py-1 rounded break-all">
                            {property.code}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          <Badge variant="secondary">
                            {t(`data_types.${property.data_type}`)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {(() => {
                            const catName = getCategoryName((property as any).property_category_uid)
                            return catName ? (
                              <Badge variant="outline">{catName}</Badge>
                            ) : (
                              <span className="text-muted-foreground/50">-</span>
                            )
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatCreated(property.created_at)}
                        </td>
                        <td className="sticky right-0 z-10 bg-card px-6 py-4 whitespace-nowrap text-right text-sm font-medium shadow-[inset_1px_0_0_0_var(--border)]">
                          <div className="flex items-center justify-end space-x-2">
                            <Link to={`/${org}/properties/${property.uid}` as any}>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`view-property-${property.uid}`}
                              >
                                <AppIcon path={mdiEyeOutline} size="sm" />
                              </Button>
                            </Link>
                            <Can perm="configuration.write">
                              <Link to={`/${org}/properties/${property.uid}/edit` as any}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`edit-property-${property.uid}`}
                                >
                                  <AppIcon path={mdiPencil} size="sm" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(property)}
                                disabled={deletePropertyMutation.isPending}
                                className="text-destructive hover:text-destructive disabled:opacity-50"
                                data-testid={`delete-property-${property.uid}`}
                              >
                                <AppIcon path={mdiTrashCanOutline} size="sm" />
                              </Button>
                            </Can>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="h-12 w-12 text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm text-muted-foreground mb-4">
                            {t('list.empty')}
                          </p>
                          <Can perm="configuration.write">
                            <Link to={`/${org}/properties/create` as any}>
                              <Button data-testid="create-first-property">
                                <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                {t('list.create_first')}
                              </Button>
                            </Link>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}